import { z } from 'zod';
import type { AskUserInput } from '../hooks/UseAgentChat/schemas';
import type { AgentQuestionItem } from '../types';

const AGENT_QUESTION_ANSWERS_PREFIX = 'Agent question answers (JSON):';

export type AnswerValue = string | string[];
export type AgentQuestionAnswer = {
  value?: AnswerValue;
  customText?: string;
};

export type AgentQuestionAnswerOption = {
  value: string;
  label: string;
};

export type ParsedAgentQuestionAnswer = {
  question: string;
  answer?: string;
  answerOption?: AgentQuestionAnswerOption | null;
  answerOptions?: AgentQuestionAnswerOption[];
};

const answerOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const normalizeOptions = (
  question: AgentQuestionItem,
  value: AnswerValue
): Array<{ value: string; label: string }> => {
  const values = Array.isArray(value) ? value : [value];
  return values.flatMap(selectedValue => {
    const option = (question.options ?? []).find(
      o => o.value === selectedValue
    );
    const parsed = answerOptionSchema.safeParse(option);
    return parsed.success ? [parsed.data] : [];
  });
};

const parsedAnswerItemSchema = z.object({
  question: z.string(),
  answer: z
    .string()
    .transform(v => v.trim() || undefined)
    .catch(undefined),
  answer_option: answerOptionSchema.nullable().catch(null),
  answer_options: z.array(answerOptionSchema.nullable().catch(null)).catch([]),
});

export const parseAgentQuestionAnswersFromMessage = (
  content: string
): ParsedAgentQuestionAnswer[] | null => {
  if (!content.startsWith(AGENT_QUESTION_ANSWERS_PREFIX)) {
    return null;
  }

  const payloadText = content
    .slice(AGENT_QUESTION_ANSWERS_PREFIX.length)
    .trim();
  if (!payloadText) {
    return null;
  }

  let parsedPayload: unknown;
  try {
    parsedPayload = JSON.parse(payloadText);
  } catch {
    return null;
  }

  if (!Array.isArray(parsedPayload)) {
    return null;
  }

  const parsedAnswers: ParsedAgentQuestionAnswer[] = parsedPayload
    .map(item => parsedAnswerItemSchema.safeParse(item))
    .filter(result => result.success)
    .map(result => {
      const { question, answer, answer_option, answer_options } = result.data;
      const parsed: ParsedAgentQuestionAnswer = {
        question,
        answerOption: answer_option,
        answerOptions: answer_options.filter(
          (o): o is { value: string; label: string } => o !== null
        ),
      };
      if (answer) {
        parsed.answer = answer;
      }
      return parsed;
    });

  return parsedAnswers.length > 0 ? parsedAnswers : null;
};

/**
 * An `ask_user` question in the shape the question form renders. The label doubles as the option
 * value: the label is what the tool takes back as the answer.
 */
export const toAgentQuestionItem = (
  input: AskUserInput
): AgentQuestionItem => ({
  question: input.question,
  options: (input.options ?? []).map(option => ({
    value: option.label,
    label: option.label,
    description: option.description,
  })),
  multi_select: input.selectionMode === 'multi_select',
});

/**
 * The answer `ask_user` resumes with: the chosen labels plus any custom text, as a list for a
 * multi-select question and joined into one string otherwise.
 */
export const formatAskUserAnswer = (
  question: AgentQuestionItem,
  answer: AgentQuestionAnswer | undefined
): string | string[] => {
  const selected =
    answer?.value === undefined
      ? []
      : Array.isArray(answer.value)
        ? answer.value
        : [answer.value];
  const customText = answer?.customText?.trim() ?? '';
  const values = [...selected, ...(customText ? [customText] : [])];
  return question.multi_select ? values : values.join(', ');
};

export const formatAgentQuestionAnswersForMessage = (
  questions: AgentQuestionItem[],
  answers: Record<number, AgentQuestionAnswer>
): string => {
  const payload = questions.map((question, index) => {
    const answer = answers[index];
    const customText = answer?.customText?.trim() ?? '';
    if ((question.options ?? []).length === 0) {
      return {
        question: question.question,
        answer: customText,
      };
    }

    const selectedOptions = normalizeOptions(question, answer?.value ?? []);
    if (question.multi_select) {
      return {
        question: question.question,
        answer_options: selectedOptions,
        answer: customText || undefined,
      };
    }

    return {
      question: question.question,
      answer_option: selectedOptions[0] ?? null,
      answer: customText || undefined,
    };
  });

  return `${AGENT_QUESTION_ANSWERS_PREFIX}\n${JSON.stringify(payload, null, 2)}`;
};
